import React, { useState, useEffect } from 'react';
import { IoShieldOutline, IoQrCodeOutline, IoKeyOutline, IoCheckmarkCircleOutline, IoAlertCircleOutline, IoRefreshOutline, IoCopyOutline, IoCheckmarkOutline } from 'react-icons/io5';
import { toast, Toaster } from 'react-hot-toast';
import jsQR from 'jsqr';
import api from '../services/api';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function TwoFactorAuthContent({ is2FAEnabled, qrCodeUrl }) {
    const { t } = useTranslation();
    const [isEnabled, setIsEnabled] = useState(is2FAEnabled || false);
    const [isLoading, setIsLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [qrCode, setQrCode] = useState('');
    const [backupCodes, setBackupCodes] = useState([]);
    const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
    const [activeTab, setActiveTab] = useState('qr');
    const [showBackupCodes, setShowBackupCodes] = useState(false);
    const [copiedStates, setCopiedStates] = useState({});
    const [accountInfo, setAccountInfo] = useState({
        accountName: '',
        secretKey: '',
        keyType: ''
    });

    useEffect(() => {
        setIsEnabled(is2FAEnabled || false);
    }, [is2FAEnabled]);

    useEffect(() => {
        if (qrCodeUrl) {
            setQrCode(qrCodeUrl);
            // QR kodundan bilgileri çıkar
            extractQRCodeInfo(qrCodeUrl);
        }
    }, [qrCodeUrl]);

    const extractQRCodeInfo = async (qrImgUrl) => {
        try {
            // Image oluştur ve QR kodu yükle
            const img = new Image();
            img.crossOrigin = 'anonymous'; // CORS için
            img.src = qrImgUrl;

            img.onload = function () {
                try {
                    // Canvas oluştur ve görüntüyü çiz
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);

                    // jsQR ile QR kodu çözme
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const qrCode = jsQR(imageData.data, canvas.width, canvas.height);

                    if (qrCode) {
                        const otpauthUri = qrCode.data;
                        console.log('QR Code Data:', otpauthUri);

                        try {
                            // otpauth URI'sini parse et
                            const url = new URL(otpauthUri);
                            const secret = url.searchParams.get('secret');
                            const issuer = url.searchParams.get('issuer');

                            // Email/account name'i çıkar
                            const pathParts = url.pathname.split('/');
                            const accountName = pathParts[pathParts.length - 1];
                            const decodedAccountName = decodeURIComponent(accountName);

                            // Domain kısmını kaldır, sadece email'i al
                            const cleanAccountName = decodedAccountName.includes(':')
                                ? decodedAccountName.split(':')[1]
                                : decodedAccountName;

                            // Bilgileri state'e kaydet
                            setAccountInfo({
                                accountName: cleanAccountName,
                                secretKey: secret || '',
                                keyType: t('twoFactorAuth.timeBasedKey')
                            });

                            console.log('Account Name:', cleanAccountName);
                            console.log('Secret Key:', secret);
                            console.log('Issuer:', issuer);

                        } catch (parseError) {
                            console.error('URI parse hatası:', parseError);
                            // Fallback: empty data
                            setAccountInfo({
                                accountName: '-',
                                secretKey: '-',
                                keyType: '-'
                            });
                        }
                    } else {
                        console.log("QR kodu çözülemedi.");
                        // Fallback: empty data
                        setAccountInfo({
                            accountName: '-',
                            secretKey: '-',
                            keyType: '-'
                        });
                    }
                } catch (canvasError) {
                    console.error('Canvas işleme hatası:', canvasError);
                    // Fallback: empty data
                    setAccountInfo({
                        accountName: '-',
                        secretKey: '-',
                        keyType: '-'
                    });
                }
            };

            img.onerror = function () {
                console.error('QR kod görüntüsü yüklenemedi');
                // Fallback: empty data
                setAccountInfo({
                    accountName: '-',
                    secretKey: '-',
                    keyType: '-'
                });
            };

        } catch (error) {
            console.error('QR kod bilgileri çıkarılırken hata:', error);
            // Fallback: empty data
            setAccountInfo({
                accountName: '-',
                secretKey: '-',
                keyType: '-'
            });
        }
    };

    const handleEnable2FA = async () => {
        setIsLoading(true);
        try {
            // Eğer QR kod zaten varsa, sadece step'i ilerlet
            if (qrCode) {
                setCurrentStep(2);
                toast.success(t('twoFactorAuth.qrReady'));
            } else {
                // QR kod yoksa yeni oluştur (bu durumda zaten yukarıda oluşturulmuş olmalı)
                setCurrentStep(2);
                toast.success(t('twoFactorAuth.setupStarted'));
            }
        } catch (error) {
            toast.error(t('twoFactorAuth.setupFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyCode = async () => {
        const codeString = verificationCode.join('');
        if (!codeString || codeString.length !== 6) {
            toast.error(t('twoFactorAuth.enterCode'));
            return;
        }

        setIsLoading(true);
        try {
            // API çağrısı yap
            const response = await api.validatePin(codeString);

            // Başarılı response kontrolü
            if (response && Array.isArray(response) && response.length > 0) {
                // Kurtarma kodlarını state'e kaydet
                setBackupCodes(response.map(item => item.code));

                setIsEnabled(true);
                setShowBackupCodes(true);
                toast.success(t('twoFactorAuth.successTitle'));
            } else {
                toast.error(t('twoFactorAuth.codeInvalid'));
            }
        } catch (error) {
            console.error('2FA doğrulama hatası:', error);
            toast.error(t('twoFactorAuth.codeInvalid'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDisable2FA = async () => {
        setIsLoading(true);
        try {
            // API çağrısı yap
            const response = await api.deactivate2Factor();

            // Başarılı response kontrolü
            if (response && response.success) {
                toast.success(t('twoFactorAuth.disableSuccess'));
                // Sayfayı refresh et
                window.location.reload();
            } else {
                toast.error(t('twoFactorAuth.disableFailed'));
            }
        } catch (error) {
            console.error('2FA devre dışı bırakma hatası:', error);
            toast.error(t('twoFactorAuth.disableFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    const copyBackupCode = async (code, index) => {
        try {
            await navigator.clipboard.writeText(code);
            toast.success(t('twoFactorAuth.backupCodeCopied'));

            // Tik işareti göster
            setCopiedStates(prev => ({
                ...prev,
                [index]: true
            }));

            // 3 saniye sonra eski haline döndür
            setTimeout(() => {
                setCopiedStates(prev => ({
                    ...prev,
                    [index]: false
                }));
            }, 3000);
        } catch (err) {
            console.error('Kopyalama hatası:', err);
            toast.error(t('twoFactorAuth.copyFailed'));
        }
    };

    const handleBackupCodesComplete = () => {
        setShowBackupCodes(false);
        toast.success(t('twoFactorAuth.setupComplete'));
    };

    const handleCodeChange = (index, value) => {
        if (value.length > 1) return; // Sadece tek karakter

        const newCode = [...verificationCode];
        newCode[index] = value;
        setVerificationCode(newCode);

        // Otomatik olarak bir sonraki input'a geç
        if (value && index < 5) {
            const nextInput = document.getElementById(`code-${index + 1}`);
            if (nextInput) {
                nextInput.focus();
            }
        }
    };

    const handleKeyDown = (index, e) => {
        // Backspace tuşu ile önceki input'a geç
        if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
            const prevInput = document.getElementById(`code-${index - 1}`);
            if (prevInput) {
                prevInput.focus();
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        const newCode = [...verificationCode];

        for (let i = 0; i < pastedData.length && i < 6; i++) {
            newCode[i] = pastedData[i];
        }

        setVerificationCode(newCode);

        // Son dolu input'a focus
        const lastFilledIndex = Math.min(pastedData.length - 1, 5);
        const nextInput = document.getElementById(`code-${lastFilledIndex}`);
        if (nextInput) {
            nextInput.focus();
        }
    };

    return (
        <div className="two-factor-content">
            {!isEnabled && !showBackupCodes && (
                <div className="row gx-3 mb-4 two-factor-top-section">
                    <div className="col-lg-4">
                        <div className="two-factor-image">
                            {qrCode ? (
                                <img src={qrCode} alt="2FA QR Code" width="100%" />
                            ) : (
                                <div className="qr-placeholder">
                                    <IoQrCodeOutline size={48} />
                                    <p>{t('twoFactorAuth.qrLoading')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col-lg-4">
                        <div className="two-factor-card">
                            <div className="verification-form">
                                <label className="form-label">
                                    {t('twoFactorAuth.codeLabel')}
                                </label>
                                <div className="code-input-container">
                                    {verificationCode.map((digit, index) => (
                                        <input
                                            key={index}
                                            id={`code-${index}`}
                                            type="text"
                                            value={digit}
                                            onChange={(e) => handleCodeChange(index, e.target.value.replace(/\D/g, ''))}
                                            onKeyDown={(e) => handleKeyDown(index, e)}
                                            onPaste={handlePaste}
                                            className="code-input"
                                            maxLength={1}
                                            autoComplete="off"
                                        />
                                    ))}
                                </div>
                                <button
                                    onClick={handleVerifyCode}
                                    disabled={isLoading || verificationCode.join('').length !== 6}
                                    className="btn-primary submit-btn submit-btn-2fa"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="spinner"></div>
                                            {t('twoFactorAuth.submitting')}
                                        </>
                                    ) : (
                                        t('twoFactorAuth.submit')
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4">
                        <div className="two-factor-card">
                            <div className="account-details">
                                <div className="detail-item">
                                    <span className="detail-label">{t('twoFactorAuth.accountName')}</span>
                                    <div className="detail-value">
                                        <span>{accountInfo.accountName || '-'}</span>
                                    </div>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">{t('twoFactorAuth.secretKey')}</span>
                                    <div className="detail-value">
                                        <span>{accountInfo.secretKey || '-'}</span>
                                    </div>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">{t('twoFactorAuth.keyType')}</span>
                                    <div className="detail-value">
                                        <span>{accountInfo.keyType || '-'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="row gx-lg-5 justify-content-center">
                <div className="col-lg-12">
                    {showBackupCodes ? (
                        // Kurtarma Kodları Ekranı
                        <div className="success-screen">
                            <div className="success-header">
                                <IoCheckmarkCircleOutline size={64} className="success-icon" />
                                <h3>{t('twoFactorAuth.successTitle')}</h3>
                                <p>{t('twoFactorAuth.successMessage')}</p>
                            </div>

                            <div className="backup-codes-section">
                                <h4>{t('twoFactorAuth.backupCodesTitle')}</h4>
                                <p className="backup-codes-info">
                                    {t('twoFactorAuth.backupCodesInfo')}
                                </p>
                                <div className="backup-codes-grid">
                                    {backupCodes.map((code, index) => (
                                        <div key={index} className="backup-code-item">
                                            <span className="code-text">{code}</span>
                                            <button
                                                className={`copy-btn ${copiedStates[index] ? 'copied' : ''}`}
                                                onClick={() => copyBackupCode(code, index)}
                                                title={t('twoFactorAuth.copy')}
                                            >
                                                {copiedStates[index] ? <IoCheckmarkOutline size={16} /> : <IoCopyOutline size={16} />}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <div className="text-center mt-4">
                                    <button
                                        onClick={handleBackupCodesComplete}
                                        className="btn-primary btn-backup-codes-complete"
                                    >
                                        {t('twoFactorAuth.completed')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : isEnabled ? (
                        // 2FA Aktif - Devre Dışı Bırakma Ekranı
                        <div className="disable-2fa-screen">
                            <h2 className="two-factor-title">{t('twoFactorAuth.disableTitle')}</h2>
                            <p className="two-factor-subtitle">
                                {t('twoFactorAuth.disableMessage1')}
                            </p>
                            <p className="two-factor-subtitle">
                                {t('twoFactorAuth.disableMessage2')}
                            </p>
                            <p className="two-factor-subtitle">
                                {t('twoFactorAuth.disableMessage3')}
                            </p>
                            <div className="disable-button-container d-flex justify-content-center">
                                <button
                                    className="btn-danger btn-disable-2fa"
                                    onClick={handleDisable2FA}
                                    disabled={isLoading}
                                >
                                    {isLoading ? t('twoFactorAuth.disabling') : t('twoFactorAuth.disable')}
                                </button>
                            </div>
                        </div>
                    ) : (
                        // 2FA Kapalı - Kurulum Ekranı
                        <div>
                            <h2 className="two-factor-title">{t('twoFactorAuth.setupTitle')}</h2>
                            <p className="two-factor-subtitle">{t('twoFactorAuth.setupMessage1')}</p>
                            <p className="two-factor-subtitle">{t('twoFactorAuth.setupMessage2')}</p>

                            {/* Success Screen - Step 3 */}
                            {currentStep === 3 ? (
                                <div className="success-screen">
                                    <div className="success-header">
                                        <IoCheckmarkCircleOutline size={64} className="success-icon" />
                                        <h3>{t('twoFactorAuth.successTitle')}</h3>
                                        <p>{t('twoFactorAuth.successMessage')}</p>
                                    </div>

                                    <div className="backup-codes-section">
                                        <h4>{t('twoFactorAuth.backupCodesTitle')}</h4>
                                        <p className="backup-codes-info">
                                            {t('twoFactorAuth.backupCodesInfo')}
                                        </p>
                                        <div className="backup-codes-grid">
                                            {backupCodes.map((code, index) => (
                                                <div key={index} className="backup-code-item">
                                                    <span className="code-text">{code}</span>
                                                    <button
                                                        className="copy-btn"
                                                        onClick={() => copyBackupCode(code)}
                                                        title={t('twoFactorAuth.copy')}
                                                    >
                                                        <IoKeyOutline size={18} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Tab Navigation */}
                                    <div className="two-factor-tabs">
                                        <button
                                            className={`tab-btn ${activeTab === 'qr' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('qr')}
                                        >
                                            <IoQrCodeOutline size={20} />
                                            <span>{t('twoFactorAuth.qrSetupTab')}</span>
                                        </button>
                                        <button
                                            className={`tab-btn ${activeTab === 'manual' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('manual')}
                                        >
                                            <IoKeyOutline size={20} />
                                            <span>{t('twoFactorAuth.manualSetupTab')}</span>
                                        </button>
                                    </div>

                                    {/* Tab Content */}
                                    <div className="tab-content">
                                        {activeTab === 'qr' && (
                                            <div className="qr-setup-content">
                                                <div className="setup-steps">
                                                    <div className="setup-step">
                                                        <div className="step-number">1</div>
                                                        <div className="step-content">
                                                            <h4>{t('twoFactorAuth.step1Title')}</h4>
                                                            <p>{t('twoFactorAuth.step1Desc')}</p>
                                                            <div className="app-downloads mt-lg-3">
                                                                <a target='_blank' href="https://apps.apple.com/us/app/google-authenticator/id388497605">
                                                                    <img src="/assets/images/new-img/appstore.png" alt="" width="130px" />
                                                                </a>
                                                                <a target='_blank' href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=tr">
                                                                    <img src="/assets/images/new-img/googleplay.png" alt="" width="130px" />
                                                                </a>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="setup-step">
                                                        <div className="step-number">2</div>
                                                        <div className="step-content">
                                                            <h4>{t('twoFactorAuth.step2TitleQR')}</h4>
                                                            <p>{t('twoFactorAuth.step2DescQR')}</p>
                                                        </div>
                                                    </div>

                                                    <div className="setup-step">
                                                        <div className="step-number">3</div>
                                                        <div className="step-content">
                                                            <h4>{t('twoFactorAuth.step3Title')}</h4>
                                                            <p>{t('twoFactorAuth.step3Desc')}</p>
                                                        </div>
                                                    </div>

                                                    <div className="setup-step">
                                                        <div className="step-number">4</div>
                                                        <div className="step-content">
                                                            <h4>{t('twoFactorAuth.step4Title')}</h4>
                                                            <p>{t('twoFactorAuth.step4Desc')}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {activeTab === 'manual' && (
                                            <div className="manual-setup-content">
                                                <div className="setup-steps">
                                                    <div className="setup-step">
                                                        <div className="step-number">1</div>
                                                        <div className="step-content">
                                                            <h4>{t('twoFactorAuth.step1Title')}</h4>
                                                            <p>{t('twoFactorAuth.step1Desc')}</p>
                                                            <div className="app-downloads mt-lg-3">
                                                                <a target='_blank' href="https://apps.apple.com/us/app/google-authenticator/id388497605">
                                                                    <img src="/assets/images/new-img/appstore.png" alt="" width="130px" />
                                                                </a>
                                                                <a target='_blank' href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=tr">
                                                                    <img src="/assets/images/new-img/googleplay.png" alt="" width="130px" />
                                                                </a>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="setup-step">
                                                        <div className="step-number">2</div>
                                                        <div className="step-content">
                                                            <h4>{t('twoFactorAuth.step2TitleManual')}</h4>
                                                            <p>{t('twoFactorAuth.step2DescManual')}</p>
                                                        </div>
                                                    </div>

                                                    <div className="setup-step">
                                                        <div className="step-number">3</div>
                                                        <div className="step-content">
                                                            <h4>{t('twoFactorAuth.step3TitleManual')}</h4>
                                                            <p>{t('twoFactorAuth.step3DescManual')}</p>
                                                        </div>
                                                    </div>

                                                    <div className="setup-step">
                                                        <div className="step-number">4</div>
                                                        <div className="step-content">
                                                            <h4>{t('twoFactorAuth.step4Title')}</h4>
                                                            <p>{t('twoFactorAuth.step4Desc')}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <Toaster
                position="top-center"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: 'var(--card-bg)',
                        color: 'var(--text)',
                        border: '1px solid var(--border)',
                    },
                }}
            />
        </div>
    );
}